import { ResponseMessage } from './types';
import { sanitizeHTML, sanitizeURL } from '../shared/utils/sanitize';

/**
 * Creates a right-oriented user message bubble and appends it to the message container.
 * @param messageContainer The container element for chat messages.
 * @param input The input textarea for user messages.
 * @param data The message content to send.
 */
function sendAndAppendMessage(
  messageContainer: HTMLDivElement,
  input: HTMLTextAreaElement,
  data: string,
  backgroundColor: string
) {
  const wrapper = document.createElement('div');
  wrapper.className = 'chat-message user';

  const avatarDiv = document.createElement('div');
  // avatarDiv.className = 'chat-message-icon';
  //   avatarDiv.innerHTML = `
  //    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 27 27" fill="none">
  //   <g clip-path="url(#clip0_983_497)">
  //     <path d="M0 13.5C0 6.04415 6.04415 0 13.5 0C20.9558 0 27 6.04415 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04415 27 0 20.9558 0 13.5Z" fill="#FFDAC2"/>
  //     <path d="M13.4827 18.6201C16.5368 18.6201 19.0127 16.1442 19.0127 13.09C19.0127 10.0359 16.5368 7.55994 13.4827 7.55994C10.4285 7.55994 7.95258 10.0359 7.95258 13.09C7.95258 16.1442 10.4285 18.6201 13.4827 18.6201Z" fill="#FF7F81"/>
  //     <path d="M13.5005 26.9999C16.7369 26.9999 19.7073 25.8612 22.0327 23.9624C21.8779 23.7779 21.6978 23.5969 21.491 23.4208C19.3674 21.612 16.4872 20.5959 13.484 20.5959C10.4808 20.5959 7.60066 21.612 5.47709 23.4208C5.27623 23.5919 5.10057 23.7675 4.94879 23.9464C7.27718 25.8548 10.2551 26.9999 13.5005 26.9999Z" fill="#DB3672"/>
  //   </g>
  //   <defs>
  //     <clipPath id="clip0_983_497">
  //       <rect width="27" height="27" fill="white"/>
  //     </clipPath>
  //   </defs>
  // </svg>
  //   `;

  const respondDiv = document.createElement('div');
  respondDiv.className = 'chat-bubble-user';
  respondDiv.style.backgroundColor = backgroundColor;

  const p = document.createElement('p');
  p.textContent = data;
  respondDiv.appendChild(p);

  wrapper.appendChild(respondDiv);
  wrapper.appendChild(avatarDiv);

  messageContainer.appendChild(wrapper);
  input.value = '';
  messageContainer.scrollTop = messageContainer.scrollHeight;
}

/**
 * Receives a response and creates a left-oriented bot message bubble in the message container.
 * @param messageContainer The container element for chat messages.
 * @param data A promise resolving to the response content.
 */
function getPrimaryMessageWrapper(
  messageContainer: HTMLDivElement,
  backgroundColor?: string
): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'chat-message';

  const respondDiv = document.createElement('div');
  respondDiv.className = 'typing-indicator chat-padding';

  // Base label + animated dots
  respondDiv.innerHTML = `
    <span class="typing-label"></span><span class="typing-dots-text">.</span>
  `;

  const labelSpan = respondDiv.querySelector('.typing-label') as HTMLSpanElement;
  const dotsSpan = respondDiv.querySelector('.typing-dots-text') as HTMLSpanElement;

  wrapper.appendChild(respondDiv);
  messageContainer.appendChild(wrapper);
  messageContainer.scrollTop = messageContainer.scrollHeight;

  // Animate dots: "." -> ".." -> "..." -> "."
  let dots = 1;
  const intervalId = window.setInterval(() => {
    if (!dotsSpan || !respondDiv.classList.contains('typing-indicator')) return;
    dots = (dots % 3) + 1;
    dotsSpan.textContent = '.'.repeat(dots);
  }, 400);

  // Store interval id so we can clear it later
  (respondDiv as any)._typingInterval = intervalId;

  // After 2s, if still typing, change label text
  window.setTimeout(() => {
    if (respondDiv && respondDiv.classList.contains('typing-indicator') && labelSpan) {
      labelSpan.textContent = 'analyzing your request';
    }
  }, 2000);

  return wrapper;
}

/**
 * Receives a response and creates a left-oriented bot message bubble in the message container.
 * @param messageContainer The container element for chat messages.
 * @param data A promise resolving to the response content.
 */
function showDots(messageContainer: HTMLDivElement, backgroundColor?: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'chat-message';

  const respondDiv = document.createElement('div');
  respondDiv.className = 'typing-indicator chat-padding';

  // Create typing container with three separate dot spans for CSS animation
  respondDiv.innerHTML = `
    <div class="typing-container">
      <span class="typing-dots-text">.</span>
      <span class="typing-dots-text">.</span>
      <span class="typing-dots-text">.</span>
      <span class="typing-label"></span>
    </div>
  `;

  wrapper.appendChild(respondDiv);
  messageContainer.appendChild(wrapper);
  messageContainer.scrollTop = messageContainer.scrollHeight;

  // Store a reference for clearing later (no JavaScript animation needed - CSS handles it)
  (respondDiv as any)._typingInterval = null;

  return wrapper;
}

/**
 * Converts simple Markdown-like syntax to HTML with URL sanitization.
 * Currently supports: **bold**, line breaks, and numbered lists.
 */
function parseMarkdownToHTML(text: string): string {
  return (
    text
      // Convert Markdown links [text](url) with URL sanitization
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        (match, linkText, url) => {
          const safeUrl = sanitizeURL(url);
          return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
        }
      )
      // Convert **bold**
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert numbered lists
      .replace(/(\d+)\.\s/g, '<br><strong>$1.</strong> ')
      // Convert dashes with ✅
      .replace(/-\s✅/g, '<br>✅')
      // Convert regular line breaks
      .replace(/\n/g, '<br>')
  );
}

/**
 * Receives a response and creates a left-oriented bot message bubble in the message container.
 * @param wrapper The outer message bubble wrapper.
 * @param messageContainer The container element for chat messages.
 * @param data A promise resolving to the response content.
 */
async function recieveAndAppendMessage(
  wrapper: HTMLDivElement,
  messageContainer: HTMLDivElement,
  data: Promise<ResponseMessage>
): Promise<void> {
  const respondDiv = wrapper.getElementsByClassName('typing-indicator')[0] as HTMLDivElement;

  if (!respondDiv) {
    console.error('Typing indicator element not found');
    return;
  }

  const response = await data;

  // No longer considered a typing indicator - this will stop the CSS animation
  respondDiv.classList.remove('typing-indicator');
  respondDiv.classList.add('chat-bubble-ai');

  const formattedContent = parseMarkdownToHTML(response.content).replace(
    /(<br\s*\/?>\s*){2,}/gi,
    '<br><br>'
  );

  // Sanitize HTML to prevent XSS attacks
  const sanitizedContent = sanitizeHTML(formattedContent);

  respondDiv.innerHTML = `
    <p class="">${sanitizedContent}</p>
  `;

  wrapper.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
}

/**
 * Returns the current time in HH:MM AM/PM format.
 */
function getCurrentTime(): string {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export { getPrimaryMessageWrapper, recieveAndAppendMessage, sendAndAppendMessage, showDots };
